import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Button, Breadcrumb, Container, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { AssignmentBox, Loading, Pagination, PracticeConfirmationModal } from "components";
import IconArrowBack from "components/Icons/IconArrowBack/IconArrowBack";
import PageHeading from "components/PageHeading/PageHeading";

import { useAppDispatch } from "store/hooks";
import { removeResults } from "store/slices/topicPractice";
import {
  setRedEnvelopeQueryParams,
  setGreenEnvelopeQueryParams,
  setOrangeEnvelopeQueryParams,
} from "store/slices/queryParams";
import { getAdmissionTestLabel } from "utils/common/assignmentDetail";

import { useLazyGetEnvelopeThemeAssignmentsQuery, useGetUserThemeByIdQuery } from "api/generated";

import { translateEnvelopeId } from "utils/translateEnvelopeId";
import { processApiError } from "utils/processApiError";
import { searchInAssignment } from "utils/searchInAssignment";
import type { ISearchAssignment } from "utils/searchInAssignment";

import { useQuery } from "utils/hooks/useQuery";
import { PAGE_QUERY_PARAM, SEARCH_QUERY_PARAM } from "const";
import { usePracticeConfirmationModalLocalStorage } from "components/Modal/PracticeConfirmationModal/usePracticeConfirmationModalLocalStorage";

interface IAssignmentData extends ISearchAssignment {
  id: string;
}

const EnvelopeSuperTopics = () => {
  const { t } = useTranslation(["common"]);
  const navigate = useNavigate();

  const { envelopeId, userThemeId } = useParams();
  if (!envelopeId || !userThemeId) {
    navigate("/");
    return null;
  }

  const dispatch = useAppDispatch();
  const { removeAllQueryParams, setEagerQueryParam, setSearchParam, searchParams } = useQuery();

  const {
    data: themeData,
    isLoading: isThemeLoading,
    error: apiThemeError,
  } = useGetUserThemeByIdQuery({ userThemeId });

  const [trigger, { data: assignmentsData, isLoading: isAssignmentsLoading, error: lazyAssignmentsError }] =
    useLazyGetEnvelopeThemeAssignmentsQuery();

  const [allAssignments, setAllAssignments] = useState<IAssignmentData[] | null>(null);
  const [foundAssignments, setFoundAssignments] = useState<IAssignmentData[] | null>(null);
  const [isModalVisible, setModalVisible] = useState(false);
  const { isConfirmationModalAllowed, setSeenModalToLocalStorage } = usePracticeConfirmationModalLocalStorage();

  const search = searchParams.get(SEARCH_QUERY_PARAM);
  const pageString = searchParams.get(PAGE_QUERY_PARAM);
  const currentPage = typeof pageString === "string" ? Number(pageString) : 1;

  useEffect(() => {
    search === null ? setFoundAssignments(allAssignments) : searchAssignments(search);
  }, [allAssignments]);

  useEffect(() => {
    if (apiThemeError === undefined) return;

    processApiError(apiThemeError);
  }, [apiThemeError]);

  useEffect(() => {
    if (lazyAssignmentsError === undefined) return;

    processApiError(lazyAssignmentsError);
  }, [lazyAssignmentsError]);

  useEffect(() => {
    const searchParam = searchParams.get(SEARCH_QUERY_PARAM);
    const pageParam = searchParams.get(PAGE_QUERY_PARAM);

    const queryParams = {
      topicId: userThemeId,
      params: {
        ...(typeof searchParam === "string" ? { [SEARCH_QUERY_PARAM]: searchParam } : null),
        ...(typeof pageParam === "string" ? { [PAGE_QUERY_PARAM]: pageParam } : null),
      },
    };

    switch (envelopeId) {
      case "cervena":
        dispatch(setRedEnvelopeQueryParams(queryParams));
        break;
      case "oranzova":
        dispatch(setOrangeEnvelopeQueryParams(queryParams));
        break;
      case "zelena":
        dispatch(setGreenEnvelopeQueryParams(queryParams));
        break;
      default:
    }
  }, [searchParams]);

  const searchAssignments = (searchedString: string) => {
    if (allAssignments === null) return;

    const found: IAssignmentData[] = [];

    for (const assignment of allAssignments) {
      if (searchInAssignment(assignment, searchedString)) {
        found.push(assignment);
      }
    }

    setFoundAssignments(found);
  };

  useEffect(() => {
    const themeId = themeData?.userTheme.theme?.id;
    if (!themeId) return;

    trigger({ themeId, envelopeId: translateEnvelopeId(envelopeId) }).catch(() => {});
  }, [themeData]);

  useEffect(() => {
    if (assignmentsData === undefined) {
      setAllAssignments([]);
      return;
    }

    const assignmentItems = assignmentsData.userAssignmentsCached.items;

    const parsedAssignments: IAssignmentData[] = [];

    for (const assignment of assignmentItems) {
      if (assignment.assignment?.isActive !== true) continue;

      parsedAssignments.push({
        id: assignment.id,
        text: assignment.assignment?.assignment?.assignmentText ?? "",
        questionText: assignment.assignment?.assignment?.questionsText ?? "",
        questionHeadings: assignment.assignment?.assignment?.questions.map(question => question.heading ?? "") ?? [],
        href: getAssignmentHref(assignment.id),
        testId: getAdmissionTestLabel(assignment.assignment?.general?.admissionTest) ?? null,
        testOrder: assignment.assignment?.general?.testOrder ?? null,
        envelopeType: assignment.envelope,
      });

      setAllAssignments(parsedAssignments);
    }
  }, [assignmentsData]);

  useEffect(() => {
    if (allAssignments === null) return;

    if (search === null || search.length < 1) {
      if (searchParams.get(SEARCH_QUERY_PARAM) !== null) {
        removeAllQueryParams();
      }
      setFoundAssignments(allAssignments);

      return;
    }

    const timer = setTimeout(() => {
      searchAssignments(search);
    }, 250);

    return () => {
      clearTimeout(timer);
    };
  }, [search]);

  const title = themeData === undefined ? "" : (themeData.userTheme.theme?.appName ?? themeData.userTheme.theme?.name);

  const getAssignmentHref = (assignmentId: string) => `/ulohy/obalka/${envelopeId}/${userThemeId}/${assignmentId}`;

  const handleSearch = (searched: string) => {
    setEagerQueryParam(SEARCH_QUERY_PARAM, searched);
  };

  const handlePageChange = (page: number) => {
    setSearchParam(PAGE_QUERY_PARAM, page.toString());
  };

  const submitForm = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
  };

  const handlePracticeStart = () => {
    dispatch(removeResults());
    navigate(`/procvicovani/obalka/${envelopeId}/tema/${userThemeId}`);
  };

  const handleSetModalVisible = () => {
    setSeenModalToLocalStorage();
    setModalVisible(true);
  };

  const pagesCount = foundAssignments === null ? 1 : Math.ceil(foundAssignments.length / 10);

  return (
    <>
      <div className={"page-header"}>
        <Container className={"container-mw-md"}>
          <Breadcrumb>
            <li className={"breadcrumb-item"}>
              <Link to={`/ulohy/obalka/${envelopeId}`}>
                <IconArrowBack />
                {t("back", { ns: "common" })}
              </Link>
            </li>
          </Breadcrumb>
          <PageHeading customTitle={title ?? ""} />
          <div className="page-header__inputs">
            <Button
              className={"text-uppercase"}
              variant="primary"
              disabled={assignmentsData === undefined || assignmentsData.userAssignmentsCached.items.length === 0}
              value={search ?? ""}
              onClick={() => {
                isConfirmationModalAllowed ? handleSetModalVisible() : handlePracticeStart();
              }}
            >
              {t("trainThisTypeOfAssignments", { ns: "assignments" })}
            </Button>

            <Form className="flex-grow-1" onSubmit={submitForm}>
              <Form.Control
                name="search"
                className={"form-search"}
                placeholder="Hledej v textu úlohy..."
                onChange={event => {
                  handleSearch(event.target.value);
                }}
              />
            </Form>
          </div>{" "}
        </Container>
      </div>

      <Container className={"container-mw-md page-container"} fluid>
        {isThemeLoading || isAssignmentsLoading ? (
          <Loading />
        ) : (
          <>
            {(foundAssignments ?? []).slice((currentPage - 1) * 10, currentPage * 10).map(assignment => (
              <AssignmentBox key={assignment.id} assignment={assignment} />
            ))}

            {pagesCount > 1 ? (
              <Pagination
                className={"mt-16px"}
                current={currentPage}
                total={pagesCount}
                onNavigation={handlePageChange}
              />
            ) : null}
          </>
        )}
      </Container>
      <PracticeConfirmationModal
        show={isModalVisible}
        onHide={() => {
          setModalVisible(false);
        }}
        onConfirm={handlePracticeStart}
      />
    </>
  );
};

export default EnvelopeSuperTopics;
